/** Justvero made-to-order leather strap configurator. */
// Surcharge keys map to the add-on product's variants by SKU. No prices here:
// they live on the variants, reach the page through the addon map the block
// prints, and are read back with #surchargePrice. A price written twice is a
// price that eventually disagrees with itself.
// What each field says when it is missing. Written out rather than generated
// from the label: "Please select your lug width (mm)" carried the unit into the
// sentence, and "select" was wrong for the boxes you type into.
const FIELD_ERRORS = {
  lug_width_mm: 'Please select your lug width',
  buckle_width_mm: 'Please select your buckle width',
  length_code: 'Please select your strap length',
  custom_long_side_mm: 'Please enter the long side in millimetres',
  custom_short_side_mm: 'Please enter the short side in millimetres',
  stitch_color: 'Please choose a stitch colour',
  engraving_text: 'Please enter the text to engrave',
  watch_brand: 'Please enter your watch brand',
  watch_model: 'Please enter your watch model, or a short note',
};

const SURCHARGES = {
  SIGNATURE: { sku: 'JV-ADDON-PADDED', label: 'Signature style' },
  CURVED: { sku: 'JV-ADDON-CURVED', label: 'Curved end' },
  ENGRAVING: { sku: 'JV-ADDON-ENGRAVING', label: 'Engraving' },
  PRE_V: { sku: 'JV-ADDON-PRE-V', label: 'Pre-V buckle' },
  BUTTERFLY: { sku: 'JV-ADDON-BUTTERFLY', label: 'Butterfly clasp' },
  HIDDEN_DEPLOYANT: { sku: 'JV-ADDON-HIDDEN-DEPLOYANT', label: 'Hidden Deployant buckle' },
};

const FINISH_LABELS = { SILVER: 'Silver', GOLD: 'Gold', ROSE_GOLD: 'Rose Gold', BLACK: 'Black' };
const BUCKLE_LABELS = { CLASSIC: 'Classic', PRE_V: 'Pre-V', BUTTERFLY: 'Butterfly', HIDDEN_DEPLOYANT: 'Hidden Deployant' };
const STYLE_LABELS = { SIGNATURE: 'Signature', SLIM: 'Slim', STITCHLESS: 'Stitchless' };
const STITCH_LABELS = {
  AS_PICTURED: 'As pictured', NO_STITCH: 'No stitch', AQUA_BLUE: 'Aqua Blue', BEIGE: 'Beige',
  BLACK: 'Black', BLUE: 'Blue', BLUE_GREY: 'Blue Grey', COGNAC: 'Cognac', DARK_BROWN: 'Dark Brown',
  DARK_GRAY: 'Dark Grey', DARK_NAVY: 'Dark Navy', GREEN: 'Green', LIGHT_BROWN: 'Light Brown',
  LIGHT_PINK: 'Light Pink', MINT: 'Mint', NAVY: 'Navy', OLIVE: 'Olive', ORANGE: 'Orange',
  PURPLE: 'Purple', RED: 'Red', TAUPE: 'Taupe', TURQUOISE: 'Turquoise', WHITE: 'White', YELLOW: 'Yellow',
};

const AXIS_SURCHARGE_KEYS = {
  style: { SIGNATURE: 'SIGNATURE' },
  buckle: { PRE_V: 'PRE_V', BUTTERFLY: 'BUTTERFLY', HIDDEN_DEPLOYANT: 'HIDDEN_DEPLOYANT' },
  watch_end: { CURVED: 'CURVED' },
};
const DEFAULT_META_MAP = {
  style: { slim: 'SLIM', padded: 'SIGNATURE', stitchless: 'STITCHLESS' },
  buckle: { classic: 'CLASSIC', 'pre-v': 'PRE_V', butterfly: 'BUTTERFLY', 'hidden-deployant': 'HIDDEN_DEPLOYANT' },
  watch_end: { straight: 'STRAIGHT', curved: 'CURVED' },
};

class LeatherStrapConfigurator extends HTMLElement {
  #addonMap = {};
  #basePrice = 0;
  #formId = '';
  #form = null;
  #initialVariantId = '';
  #configurationId = '';
  #formObserver = null;
  #dialogTriggers = new WeakMap();
  #galleryThumbBindings = [];
  #defaults = { style: null, buckle: null, watch_end: null };

  connectedCallback() {
    this.#formId = this.dataset.formId || '';
    this.#initialVariantId = this.dataset.initialVariantId || '';
    this.#configurationId = globalThis.crypto?.randomUUID?.() || `jv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.#basePrice = (parseInt(this.dataset.basePriceCents || '0', 10) || 0) / 100;
    try {
      const map = this.querySelector('script[data-addon-map]');
      if (map) this.#addonMap = JSON.parse(map.textContent || '{}');
    } catch { /* fail closed for paid selections */ }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('mousedown', this.#onFinishToggle);
    this.addEventListener('click', this.#onFinishToggle);
    for (const dialog of this.querySelectorAll('dialog[data-cfg-dialog]')) {
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        const trigger = this.#dialogTriggers.get(dialog);
        trigger?.focus();
        this.#dialogTriggers.delete(dialog);
      });
    }
    this.#parseDefaults();
    this.#bindProductFormSafetyGate();
    this.#bindProductGalleryThumbnails();
    this.#syncBuckleFamily();
    this.#syncWatchHelp();
    this.#syncConditionalControls();
    this.#syncHiddenProps();
    this.#updateSummary();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mousedown', this.#onFinishToggle);
    this.removeEventListener('click', this.#onFinishToggle);
    this.#form?.removeEventListener('submit', this.#handleProductFormSubmit, true);
    window.removeEventListener('click', this.#handleAddToCartClick, true);
    document.removeEventListener('submit', this.#handleProductFormSubmit, true);
    window.removeEventListener('submit', this.#handleProductFormSubmit, true);
    this.#form?.removeEventListener('change', this.#handleExternalFormChange);
    this.#formObserver?.disconnect();
    for (const [thumbnail, handler] of this.#galleryThumbBindings) {
      thumbnail.removeEventListener('click', handler, true);
    }
    this.#galleryThumbBindings = [];
  }

  #bindProductGalleryThumbnails() {
    if (!customElements.get('slideshow-component')) {
      customElements.whenDefined('slideshow-component').then(() => {
        if (this.isConnected) this.#bindProductGalleryThumbnails();
      });
      return;
    }
    if (this.#galleryThumbBindings.length) return;

    const section = this.closest('.shopify-section') || document;
    const slideshow = section.querySelector('media-gallery slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const thumbnails = section.querySelectorAll(
      'media-gallery slideshow-controls.thumbnails .slideshow-controls__thumbnail'
    );
    thumbnails.forEach((thumbnail, index) => {
      const handler = event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        slideshow.select(index, event, { animate: false });
      };
      thumbnail.addEventListener('click', handler, true);
      this.#galleryThumbBindings.push([thumbnail, handler]);
    });
  }

  #finishWasChecked = false;
  #onFinishToggle = event => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    const label = el.closest('label');
    const radio = el.dataset?.cfgField === 'buckle_finish' ? el
      : label?.querySelector('input[data-cfg-field="buckle_finish"]');
    if (!(radio instanceof HTMLInputElement)) return;
    const family = this.#radioVal('buckle_family') || 'CLASSIC';
    if (this.#defaults.buckle !== family) return;
    if (event.type === 'mousedown') { this.#finishWasChecked = radio.checked; return; }
    if (event.type === 'click' && this.#finishWasChecked) {
      radio.checked = false;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #onClick = event => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button) return;
    const dialogId = button.dataset.dialogOpen;
    if (dialogId) {
      const dialog = this.querySelector(`#${CSS.escape(dialogId)}`);
      if (dialog instanceof HTMLDialogElement) {
        this.#dialogTriggers.set(dialog, button);
        dialog.showModal();
      }
      return;
    }
    if (button.matches('[data-customizer-save]')) {
      const dialog = button.closest('dialog');
      const invalid = dialog?.querySelector('select:invalid, input:invalid');
      if (invalid instanceof HTMLInputElement || invalid instanceof HTMLSelectElement) {
        invalid.reportValidity();
        invalid.focus();
        return;
      }
      dialog?.close();
      return;
    }
    if (button.matches('[data-dialog-close]')) button.closest('dialog')?.close();

  };

  #onChange = event => {
    const element = event.target;
    if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement)) return;
    const field = element.dataset.cfgField;

    // Clear field-level error when user interacts.
    if (field) this.#clearFieldError(field);

    if (field === 'length_code') {
      const custom = element.value === 'CUSTOM';
      const panel = this.querySelector('[data-conditional="length_custom"]');
      panel?.classList.toggle('hidden', !custom);
      for (const input of panel?.querySelectorAll('input') || []) {
        input.required = custom;
        if (!custom) input.value = '';
      }
    }
    if (field === 'style' || field === 'stitch_color_mode') this.#syncConditionalControls();
    if (field === 'buckle_family') this.#syncBuckleFamily();
    if (field === 'watch_help') this.#syncWatchHelp();
    if (element.matches('[data-watch-photo]')) {
      const fileName = this.querySelector('[data-file-name]');
      const file = element.files?.[0];
      if (fileName) {
        if (file && !/^image\/(jpeg|png|webp)$/.test(file.type)) {
          fileName.textContent = 'Please use JPEG, PNG, or WebP.';
          fileName.classList.add('has-file');
          element.value = '';
        } else if (file && file.size > 8 * 1024 * 1024) {
          fileName.textContent = 'Photo must be under 8 MB.';
          fileName.classList.add('has-file');
          element.value = '';
        } else {
          fileName.textContent = file?.name || 'No photo selected.';
          if (file) fileName.classList.add('has-file');
          else fileName.classList.remove('has-file');
        }
      }

      // Start shrinking as soon as the photo is chosen. Decoding and encoding
      // used to sit on the add-to-cart path, where the customer is waiting.
      // A later selection replaces this outright, so a discarded photo's work
      // is simply dropped.
      const accepted = element.files?.[0];
      this.#photoPrepared = accepted ? { file: accepted, promise: this.#resizePhoto(accepted) } : null;
    }
    if (field === 'engraving') {
      const panel = this.querySelector('[data-conditional="engraving"]');
      panel?.classList.toggle('hidden', !element.checked);
      const text = panel?.querySelector('[data-cfg-field="engraving_text"]');
      if (text instanceof HTMLInputElement) {
        text.required = element.checked;
        if (!element.checked) text.value = '';
        if (element.checked) text.focus();
      }
    }
    this.#syncHiddenProps();
    this.#updateSummary();
  };

  #onInput = event => {
    const element = event.target;
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) return;
    if (element.dataset.cfgField === 'engraving_text') {
      const count = this.querySelector('[data-engrave-count]');
      if (count) count.textContent = `${element.value.length} / 12`;
    }
    this.#syncHiddenProps();
    this.#updateSummary();
  };

  /**
   * Brings the watch-details panel in line with its toggle.
   *
   * Runs at connect as well as on change, because the toggle's starting state
   * is a block setting now rather than always on. Reading it from the checkbox
   * rather than assuming keeps the two in step whichever way the setting is
   * left — the block renders the panel hidden and its inputs disabled to match,
   * so nothing flashes before this runs.
   */
  /**
   * Finds an element that belongs to this configurator but may be rendered
   * outside it.
   *
   * The build summary is its own block now, placed below the checkout badges,
   * so it is a sibling rather than a descendant. The search is bounded to the
   * enclosing section: two configurators on one page would otherwise write
   * into each other's summary, and a document-wide query would make that
   * silent. Nothing found means nothing to fill — the block is optional.
   */
  #scopeRoot() {
    // Not `[data-section-id]`: this element carries that attribute itself, and
    // closest() starts at the element, so the scope collapsed to the
    // configurator and the build summary — a sibling block — was never found.
    // The failure was silent by design, which is how it reached live.
    return this.closest('.shopify-section') ?? this.parentElement?.closest('[data-section-id]') ?? this;
  }

  #inSection(selector) {
    return this.querySelector(selector) ?? this.#scopeRoot().querySelector(selector);
  }

  #allInSection(selector) {
    return this.#scopeRoot().querySelectorAll(selector);
  }

  #syncWatchHelp() {
    const toggle = this.querySelector('[data-cfg-field="watch_help"]');
    if (!(toggle instanceof HTMLInputElement)) return;

    const enabled = toggle.checked;
    const panel = this.querySelector('[data-conditional="watch_help"]');
    panel?.classList.toggle('hidden', !enabled);

    for (const control of panel?.querySelectorAll('input') || []) {
      control.disabled = !enabled;
      const needsWatchText = control.matches('[data-cfg-field="watch_brand"], [data-cfg-field="watch_model"]');
      control.required = enabled && needsWatchText;
      if (!enabled) control.value = '';
    }

    if (!enabled) {
      // Clearing the file input already stops the photo being sent. Drop the
      // prepared copy too: it holds the original and a resized blob, and after
      // switching the toggle off neither is going anywhere.
      this.#photoPrepared = null;
    }

    const fileName = this.querySelector('[data-file-name]');
    if (fileName) {
      fileName.textContent = '';
      fileName.classList.remove('has-file');
    }
  }

  #syncConditionalControls() {
    const style = this.#radioVal('style') || 'SLIM';
    const mode = this.#radioVal('stitch_color_mode') || 'AS_PICTURED';
    const modeRadios = this.querySelectorAll('input[data-cfg-field="stitch_color_mode"]');
    const customPanel = this.querySelector('[data-conditional="stitch_custom"]');
    const customRadios = customPanel?.querySelectorAll('input[type="radio"]') || [];
    const stitchless = style === 'STITCHLESS';
    modeRadios.forEach(radio => { radio.disabled = stitchless; });
    this.querySelector('[data-no-stitch]')?.classList.toggle('hidden', !stitchless);
    const showCustom = !stitchless && mode === 'CUSTOM';
    customPanel?.classList.toggle('hidden', !showCustom);
    customRadios.forEach((radio, index) => {
      radio.disabled = !showCustom;
      radio.required = showCustom && index === 0;
      if (stitchless) radio.checked = false;
    });
  }

  #syncBuckleFamily() {
    const family = this.#radioVal('buckle_family') || 'CLASSIC';
    const isDefaultFamily = this.#defaults.buckle === family;
    for (const panel of this.querySelectorAll('[data-buckle-panel]')) {
      const active = panel.dataset.bucklePanel === family;
      panel.classList.toggle('hidden', !active);
      const finishes = panel.querySelectorAll('input[data-cfg-field="buckle_finish"]');
      finishes.forEach(input => { input.disabled = !active; });
      if (active) {
        if (isDefaultFamily) {
          this.#syncDefaultBuckleFinish();
        } else if (![...finishes].some(input => input.checked)) {
          finishes[0]?.click();
        }
      }
    }
  }

  #bindProductFormSafetyGate() {
    this.#form = document.getElementById(this.#formId);
    if (!(this.#form instanceof HTMLFormElement)) return;
    this.#form.addEventListener('submit', this.#handleProductFormSubmit, true);
    document.addEventListener('submit', this.#handleProductFormSubmit, true);
    window.addEventListener('submit', this.#handleProductFormSubmit, true);
    // The theme animates a thumbnail flying to the cart icon from
    // ProductFormComponent#handleClick, wired by `on:click="/handleClick"` and
    // dispatched by the component framework through
    // `document.addEventListener('click', …, { capture: true })`. It fires on
    // the click, before any submit, so an incomplete build used to get the
    // animation and then the refusal.
    //
    // Window, in capture, because the capture path runs window → document:
    // registering on document would queue behind the framework's listener,
    // which is installed when the theme's scripts load, long before this block.
    window.addEventListener('click', this.#handleAddToCartClick, true);
    this.#form.addEventListener('change', this.#handleExternalFormChange);

    const component = this.#form.closest('product-form-component');
    if (!component) return;
    // The observer used to re-disable the button whenever the theme enabled it
    // while the build was incomplete. That is the behaviour being removed, and
    // the submit handler — bound on the form, the document and the window, all
    // in capture — is what actually refuses an incomplete add.
    this.#formObserver = new MutationObserver(() => {
      const { unresolved } = this.#calcTotal(this.#getState());
      if (unresolved.length === 0) return;
      const button = component.querySelector('[ref="addToCartButton"]');
      if (button instanceof HTMLButtonElement && !button.disabled) this.#setBusyState(true);
    });
    this.#formObserver.observe(component, { subtree: true, attributes: true, attributeFilter: ['disabled', 'value'] });
  }

  #handleExternalFormChange = () => queueMicrotask(() => {
    this.#syncHiddenProps();
    this.#updateSummary();
  });

  /**
   * Stops a click on add to cart before the theme reacts to it, when the build
   * is not finished.
   *
   * Nothing happens here when it is: the theme animates, submits, and
   * #handleProductFormSubmit takes over as before. This only exists so that an
   * incomplete build gets the validation and nothing else — a thumbnail flying
   * to the cart says the opposite of what is about to happen.
   */
  #handleAddToCartClick = event => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[ref="addToCartButton"]');
    if (!button) return;

    // Only this product's button: a page can hold a quick-add form too.
    //
    // Compared by identity, not by id. HTMLFormElement exposes its named
    // controls as properties, and the product form carries
    // <input type="hidden" name="id"> for the variant — so `form.id` is that
    // input element, not the string. The comparison could never be true and
    // this gate returned early on every click, which is why the animation
    // still played. #handleProductFormSubmit was unaffected because it already
    // compared elements.
    if (button.closest('form') !== this.#form) return;

    if (!this.#shouldBlockCheckout()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    this.#syncHiddenProps();
    this.#updateSummary();
    this.#showValidation();
  };

  #handleProductFormSubmit = event => {
    if (event.target !== this.#form) return;
    this.#syncHiddenProps();
    this.#updateSummary();
    if (this.#shouldBlockCheckout()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showValidation();
      return;
    }

    // Clear any lingering errors before submitting.
    this.#clearAllFieldErrors();

    // Always intercept: we handle the cart add ourselves for atomic multi-item.
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#atomicCartAdd();
  };

  /** Atomic multi-item cart add: parent strap + add-on variants in one request.
   *  Properties mirror the hidden inputs exactly (contract §3.1). */
  async #atomicCartAdd() {
    const state = this.#getState();
    const { activeSurcharges } = this.#calcTotal(state);
    const variantId = this.#form?.querySelector('input[name="id"]')?.value;
    if (!variantId) return;

    // Generate a fresh configuration ID for every add-to-cart (prevents merging).
    this.#configurationId = globalThis.crypto?.randomUUID?.() || `jv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const buildId = this.#configurationId;

    // Human-readable label maps for machine values. BUCKLE_LABELS is module-scoped
    // so the build summary and the cart payload cannot drift apart.
    const END_LABELS = { STRAIGHT: 'Straight', CURVED: 'Curved' };
    const SPRING_LABELS = { QUICK_RELEASE: 'Quick-release' };

    // Build properties — human-readable values for customer-facing display.
    const parentProps = {
      // Private routing properties
      '_configuration_id': buildId,
      '_role': 'parent',
      '_estimated_total': state.config_total_snapshot,
      // Public build properties
      'Color': state.color,
      'Lug Width (mm)': state.lug_width_mm,
      'Buckle Width (mm)': state.buckle_width_mm,
      'Length': state.length_label,
      'Watch End': (END_LABELS[state.watch_end_shape] || state.watch_end_shape) + (this.#isDefault('watch_end', state.watch_end_shape) ? ' — as pictured' : ''),
      'Style': (STYLE_LABELS[state.style] || state.style) + (this.#isDefault('style', state.style) ? ' — as pictured' : ''),
      'Buckle': this.#isDefault('buckle', state.buckle_type) && state.buckle_finish === 'AS_PICTURED'
        ? `${BUCKLE_LABELS[state.buckle_type] || state.buckle_type} — as pictured`
        : `${BUCKLE_LABELS[state.buckle_type] || state.buckle_type} ${FINISH_LABELS[state.buckle_finish] || state.buckle_finish}`.trim(),
      'Spring Bars': SPRING_LABELS[state.spring_bar_type] || state.spring_bar_type,
      'Stitch Colour': STITCH_LABELS[state.stitch_color] || state.stitch_color,
    };
    // Conditional properties — only include when active (suppress false/No).
    if (state.length_code === 'CUSTOM') {
      parentProps['Custom Long Side (mm)'] = state.custom_long_side_mm;
      parentProps['Custom Short Side (mm)'] = state.custom_short_side_mm;
    }
    if (state.engraving === 'true' && state.engraving_text) {
      parentProps['Engraving'] = state.engraving_text;
    }
    if (state.watch_help === 'true') {
      if (state.watch_brand) parentProps['Watch Brand'] = state.watch_brand;
      if (state.watch_model) parentProps['Watch Model / Notes'] = state.watch_model;
      parentProps['Fit Review'] = 'Requested';
    }

    // Remove blank/empty/false properties (Shopify shows them otherwise)
    for (const [key, value] of Object.entries(parentProps)) {
      if (value === '' || value === undefined || value === null || value === 'false') delete parentProps[key];
    }

    // Collect the optional watch photo file.
    const photoInput = this.querySelector('[data-watch-photo]');
    const photoFile = photoInput instanceof HTMLInputElement ? photoInput.files?.[0] : null;
    const hasPhoto = photoFile && /^image\/(jpeg|png|webp)$/.test(photoFile.type) && photoFile.size <= 8 * 1024 * 1024;

    const addonItems = [];
    const baseSku = this.dataset.baseSku || '';
    for (const surcharge of activeSurcharges) {
      const addon = this.#addonMap[surcharge.sku];
      if (!addon?.id) continue;
      addonItems.push({
        id: addon.id,
        quantity: 1,
        properties: {
          '_configuration_id': buildId,
          '_role': 'addon',
          '_addon_type': surcharge.key,
          '_addon_label': surcharge.label,
          '_parent_sku': baseSku,
        },
      });
    }

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(c => {
      if (c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
    });

    this.#setBusyState(true);
    // Say so on the button the moment the tap lands. The request behind this
    // can run for seconds on a photo add, and until now nothing on screen
    // acknowledged the tap at all.
    this.#setAtcBusyLabel(hasPhoto ? 'Uploading photo…' : 'Adding…');
    const productForm = this.#form?.closest('product-form-component');

    let resolveEvent, rejectEvent;
    const eventPromise = new Promise((res, rej) => { resolveEvent = res; rejectEvent = rej; });

    try {
      const evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      evt.action = 'add';
      evt.context = 'product';
      const allLines = [{ merchandiseId: variantId, quantity: 1 }, ...addonItems.map(i => ({ merchandiseId: String(i.id), quantity: i.quantity }))];
      evt.lines = allLines;
      evt.promise = eventPromise;
      (productForm || this).dispatchEvent(evt);
    } catch { /* event dispatch is best-effort */ }

    // Airplane mode switched on mid-upload leaves the request hanging: iOS holds
    // the socket until TCP gives up, tens of seconds later, so nothing rejects
    // and the drawer sits on "Adding your strap…" forever. One budget covers
    // the whole submit, both requests of the photo path included.
    const abortController = new AbortController();
    const abortTimer = setTimeout(() => abortController.abort(), 25000);

    try {
      let lastData;

      if (hasPhoto) {
        const resized = await this.#preparedPhoto(photoFile);
        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', '1');
        for (const [k, v] of Object.entries(parentProps)) fd.append(`properties[${k}]`, v);
        // The re-encoded file carries its own .jpg name; the property key is
        // untouched, so Shopify still returns the /uploads/ URL the workshop
        // export and the order email read.
        fd.append('properties[_watch_photo]', resized, resized.name || photoFile.name);
        if (!addonItems.length && sectionIds.length) fd.append('sections', sectionIds.join(','));
        const parentResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd,
          signal: abortController.signal,
        });
        const parentData = await parentResp.json();
        if (!parentResp.ok || parentData.status) {
          console.error('[jv-cfg] Parent cart add failed:', parentData);
          const status = this.querySelector('[data-sr-status]');
          if (status) status.textContent = parentData.description || parentData.message || 'Could not add to cart. Please try again.';
          rejectEvent?.(new Error(parentData.description || 'Cart add failed'));
          return;
        }
        if (addonItems.length) {
          const addonResp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: addonItems, sections: sectionIds.join(',') }),
            signal: abortController.signal,
          });
          lastData = await addonResp.json();
        } else {
          lastData = parentData;
        }
      } else {
        const items = [{ id: parseInt(variantId, 10), quantity: 1, properties: parentProps }, ...addonItems];
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
          signal: abortController.signal,
        });
        lastData = await response.json();
        if (!response.ok || lastData.status) {
          console.error('[jv-cfg] Cart add failed:', lastData);
          const status = this.querySelector('[data-sr-status]');
          if (status) status.textContent = lastData.description || lastData.message || 'Could not add to cart. Please try again.';
          rejectEvent?.(new Error(lastData.description || 'Cart add failed'));
          return;
        }
      }

      resolveEvent?.({
        detail: {
          sections: lastData?.sections || {},
          source: 'jv-configurator',
          didError: false,
        },
      });

      this.#configurationId = globalThis.crypto?.randomUUID?.() || `jv-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    } catch (err) {
      console.error('[jv-cfg] Cart add error:', err);
      // A thrown fetch — the connection dropping mid-upload — used to leave
      // nothing on screen: the rejected-response paths above write to the
      // status line, this one did not. It matters more now that the drawer
      // opens on the tap and closes again when an add against an empty cart
      // fails, because the message has to survive here, next to the button.
      const status = this.querySelector('[data-sr-status]');
      if (status) status.textContent = 'Could not add to cart. Please check your connection and try again.';
      rejectEvent?.(err);
    } finally {
      clearTimeout(abortTimer);
      this.#setAtcBusyLabel(null);
      this.#setBusyState(false);
    }
  }

  /**
   * Shrinks a watch photo to something that uploads in a reasonable time.
   *
   * Always re-encodes: the previous version returned anything already under
   * 2000px untouched, which let full-size phone photos through, and it kept PNG
   * input as PNG, where `quality` does nothing and a photo stays enormous.
   *
   * Every failure path returns the original file. A slow add is a bad add; a
   * broken one is worse.
   */
  async #resizePhoto(file) {
    const MAX_EDGE = 1200;
    const QUALITY = 0.75;

    try {
      // `from-image` bakes the EXIF rotation into the pixels. It matters now in
      // a way it did not before: small photos used to be forwarded untouched
      // and the viewer applied the tag, but everything goes through the canvas
      // from here, and a canvas draws the raw buffer. Without this an iPhone
      // portrait reaches the workshop on its side.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

      const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);

      const canvas = new OffscreenCanvas(w, h);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close();

      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY });

      // Safari hands back PNG when it cannot encode what was asked for, so
      // trust the blob and not the argument. And if the re-encode came out
      // larger — a small or already-optimised file — the original is better.
      if (blob.type !== 'image/jpeg' || blob.size >= file.size) {
        return file;
      }

      const name = `${file.name.replace(/\.[^.]+$/, '')}.jpg`;
      return new File([blob], name, { type: 'image/jpeg' });
    } catch (error) {
      console.warn('[jv-cfg] Photo re-encode failed, sending the original:', error);
      return file;
    }
  }


  #getState() {
    const lengthSelect = this.querySelector('[data-cfg-field="length_code"]');
    const selectedLength = lengthSelect instanceof HTMLSelectElement ? lengthSelect.selectedOptions[0] : null;
    const style = this.#radioVal('style') || 'SLIM';
    const buckleType = this.#radioVal('buckle_family') || 'CLASSIC';
    const rawFinish = this.#radioVal('buckle_finish');
    const buckleFinish = rawFinish || (this.#defaults.buckle === buckleType ? 'AS_PICTURED' : 'SILVER');
    const stitchMode = style === 'STITCHLESS' ? 'NO_STITCH' : (this.#radioVal('stitch_color_mode') || 'AS_PICTURED');
    const state = {
      color: this.dataset.colorLabel || '',
      lug_width_mm: this.#selectVal('lug_width_mm'),
      buckle_width_mm: this.#selectVal('buckle_width_mm'),
      length_code: this.#selectVal('length_code'),
      length_label: this.#selectVal('length_code') ? (selectedLength?.textContent?.trim() || '') : '',
      watch_end_shape: this.#radioVal('watch_end_shape') || 'STRAIGHT',
      style,
      buckle_type: buckleType,
      buckle_finish: buckleFinish,
      spring_bar_type: 'QUICK_RELEASE',
      stitch_color: stitchMode === 'CUSTOM' ? this.#radioVal('stitch_color_custom') : stitchMode,
      engraving: this.#checked('engraving') ? 'true' : 'false',
      watch_brand: this.#inputVal('watch_brand'),
      watch_model: this.#inputVal('watch_model'),
      watch_help: this.#checked('watch_help') ? 'true' : 'false',
      configuration_id: this.#configurationId,
      role: 'parent',
    };
    if (state.length_code === 'CUSTOM') {
      state.custom_long_side_mm = this.#inputVal('custom_long_side_mm');
      state.custom_short_side_mm = this.#inputVal('custom_short_side_mm');
      state.length_label = `Custom — ${state.custom_long_side_mm || '?'} / ${state.custom_short_side_mm || '?'} mm`;
    }
    if (state.engraving === 'true') state.engraving_text = this.#inputVal('engraving_text');

    const { total } = this.#calcTotal(state);
    state.config_total_snapshot = total.toFixed(2);
    return state;
  }

  #calcTotal(state) {
    const activeSurcharges = [];
    // The amount comes from the variant, same as the price beside each option.
    // It used to come from a constant here while the option labels used the
    // variant, so changing a price in the admin quietly desynced the estimated
    // total from what the customer was actually charged.
    const add = (key) => activeSurcharges.push({ key, ...SURCHARGES[key], amount: this.#surchargePrice(key) });

    if (state.style === 'SIGNATURE' && !this.#isIncluded('style', 'SIGNATURE')) add('SIGNATURE');
    if (state.watch_end_shape === 'CURVED' && !this.#isIncluded('watch_end', 'CURVED')) add('CURVED');
    if (state.engraving === 'true') add('ENGRAVING');
    if (state.buckle_type === 'PRE_V' && !this.#isIncluded('buckle', 'PRE_V')) add('PRE_V');
    if (state.buckle_type === 'BUTTERFLY' && !this.#isIncluded('buckle', 'BUTTERFLY')) add('BUTTERFLY');
    if (state.buckle_type === 'HIDDEN_DEPLOYANT' && !this.#isIncluded('buckle', 'HIDDEN_DEPLOYANT')) add('HIDDEN_DEPLOYANT');

    const unresolved = activeSurcharges.filter(item => {
      const addon = this.#addonMap[item.sku];
      return !addon?.id || !addon.available;
    });
    return {
      total: this.#basePrice + activeSurcharges.reduce((sum, item) => sum + item.amount, 0),
      activeSurcharges,
      unresolved,
    };
  }

  #syncHiddenProps() {
    const state = this.#getState();
    for (const input of this.querySelectorAll('.jv-cfg__hidden-props input[data-prop]')) {
      const value = state[input.dataset.prop];
      if (value === undefined || value === null || value === '') {
        input.disabled = true;
        input.value = '';
      } else {
        input.disabled = false;
        input.value = String(value);
      }
    }
  }

  #updateSummary() {
    const state = this.#getState();
    const { total, activeSurcharges, unresolved } = this.#calcTotal(state);
    // Every match, not the first. While the owner moves the summary into an
    // accordion row the old block and the new row both exist, and filling only
    // the first would leave whichever came second looking broken — which is the
    // one being checked.
    const summaries = this.#allInSection('[data-summary-list]');
    if (summaries.length) {
      let html = '';
      html += this.#summaryRow('Color', this.#escapeHtml(state.color));
      // The three required measurements keep their row whether or not they have
      // been answered. Dropping them made the summary shorter and told the
      // customer nothing about what was still holding up the add; a dash says
      // the question exists and is open.
      const pending = '<span class="jv-cfg__summary-pending">—</span>';
      html += this.#summaryRow('Lug width', state.lug_width_mm ? `${state.lug_width_mm} mm` : pending);
      html += this.#summaryRow('Buckle width', state.buckle_width_mm ? `${state.buckle_width_mm} mm` : pending);
      html += this.#summaryRow('Length', state.length_label ? this.#escapeHtml(state.length_label) : pending);
      html += this.#summaryRow('Watch end', this.#summaryOptionValue('watch_end', state.watch_end_shape, state.watch_end_shape === 'CURVED' ? 'Curved' : 'Straight'));
      html += this.#summaryRow('Style', this.#summaryOptionValue('style', state.style, STYLE_LABELS[state.style] || state.style));
      const buckleName = BUCKLE_LABELS[state.buckle_type] || state.buckle_type;
      if (this.#isDefault('buckle', state.buckle_type) && state.buckle_finish === 'AS_PICTURED') {
        html += this.#summaryRow('Buckle', `${buckleName} (as pictured)`);
      } else {
        const finishLabel = FINISH_LABELS[state.buckle_finish] || state.buckle_finish;
        const bucklePrice = this.#getAxisOptionPrice('buckle', state.buckle_type);
        const buckleSurcharge = (!this.#isIncluded('buckle', state.buckle_type) && bucklePrice > 0) ? ` <span class="jv-cfg__surcharge">${this.#formatSurcharge(bucklePrice)}</span>` : '';
        html += this.#summaryRow('Buckle', `${buckleName} ${finishLabel}${buckleSurcharge}`);
      }
      html += this.#summaryRow('Stitch', STITCH_LABELS[state.stitch_color] || state.stitch_color || 'Select a colour');
      html += this.#summaryRow('Spring bars', 'Quick-release (included)');
      if (state.engraving === 'true') html += this.#summaryRow('Engraving', `"${this.#escapeHtml(state.engraving_text || '')}" <span class="jv-cfg__surcharge">${this.#formatSurcharge(this.#surchargePrice('ENGRAVING'))}</span>`);
      if (state.watch_help === 'true') html += this.#summaryRow('Fit review', this.#escapeHtml([state.watch_brand, state.watch_model].filter(Boolean).join(' · ') || 'Watch photo attached'));
      for (const list of summaries) list.innerHTML = html;
    }

    const endLabel = state.watch_end_shape === 'CURVED' ? 'Curved' : 'Straight';
    const finishCompact = state.buckle_finish === 'AS_PICTURED' ? '' : (FINISH_LABELS[state.buckle_finish] || '');
    const buckleLabel = `${BUCKLE_LABELS[state.buckle_type] || state.buckle_type} ${finishCompact}`.trim();
    const customSummary = `${endLabel} · ${STYLE_LABELS[state.style]} · ${buckleLabel} · ${STITCH_LABELS[state.stitch_color] || 'Select stitch'}`;
    this.querySelectorAll('[data-custom-summary], [data-modal-summary]').forEach(element => { element.textContent = customSummary; });
    // The total shows twice now — above add to cart and in the build summary's
    // own header, which sits outside this element — so the search starts at the
    // section rather than here.
    this.#allInSection('[data-total-display], [data-modal-total]').forEach(element => { element.textContent = this.#formatMoney(total); });

    const missing = this.#getMissingFields(state);
    const complete = missing.length === 0;
    const hasUnresolved = unresolved.length > 0;

    this.querySelector('[data-addon-warning]')?.classList.toggle('hidden', !hasUnresolved);
    // Missing fields no longer grey the button out. A disabled button cannot be
    // clicked, so the validation below it — the red borders, the message under
    // each field, the scroll — was unreachable: the customer saw a dead control
    // and no reason for it. The button stays live and #handleProductFormSubmit
    // is what refuses the add.
    //
    // An unavailable add-on is different in kind. Nothing the customer types
    // fixes it, so the button does stay disabled there, with [data-addon-warning]
    // saying why.
    this.#setBusyState(hasUnresolved);

    const status = this.querySelector('[data-sr-status]');
    if (status) {
      if (!complete) status.textContent = `Complete all required fields: ${missing.map(f => f.label).join(', ')}. Estimated total ${this.#formatMoney(total)}.`;
      else if (hasUnresolved) status.textContent = `Estimated total ${this.#formatMoney(total)}. Some add-on variants are unavailable.`;
      else status.textContent = `Configuration complete. Estimated total ${this.#formatMoney(total)}.`;
    }
  }

  #isComplete(state) {
    const requiredControls = this.querySelectorAll('select[required], input[required]');
    if ([...requiredControls].some(control => !control.checkValidity())) return false;
    if (!state.lug_width_mm || !state.buckle_width_mm || !state.length_code || !state.buckle_type) return false;
    if (!state.buckle_finish && this.#defaults.buckle !== state.buckle_type) return false;
    if (state.length_code === 'CUSTOM' && (!state.custom_long_side_mm || !state.custom_short_side_mm)) return false;
    if (!state.stitch_color) return false;
    if (state.engraving === 'true' && !state.engraving_text) return false;

    const currentVariant = this.#form?.querySelector('input[name="id"]')?.value || '';
    if (this.#initialVariantId && currentVariant && currentVariant !== this.#initialVariantId) return false;
    return true;
  }

  #shouldBlockCheckout() {
    const state = this.#getState();
    const { unresolved } = this.#calcTotal(state);
    return !this.#isComplete(state) || unresolved.length > 0;
  }

  /**
   * The photo being prepared, keyed by the exact File it came from, so a submit
   * that lands mid-encode awaits the same work instead of starting it again.
   * @type {{ file: File, promise: Promise<File> } | null}
   */
  #photoPrepared = null;

  /** Awaits the encode started at selection time, or does it now if it wasn't. */
  async #preparedPhoto(file) {
    if (this.#photoPrepared?.file === file) return this.#photoPrepared.promise;
    return this.#resizePhoto(file);
  }

  /**
   * Swaps the add-to-cart label while a submit is in flight.
   *
   * Separate from #setBusyState, which owns the disabled attribute: the label
   * says what is happening, the attribute stops a second submit.
   *
   * @param {string | null} label - Busy text, or null to restore.
   */
  #setAtcBusyLabel(label) {
    const form = document.getElementById(this.#formId);
    const button = form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const target = button.querySelector('.button-text') ?? button;

    if (label) {
      if (button.dataset.jvAtcLabel === undefined) button.dataset.jvAtcLabel = target.textContent ?? '';
      target.textContent = label;
      button.setAttribute('aria-busy', 'true');
      return;
    }

    if (button.dataset.jvAtcLabel !== undefined) {
      target.textContent = button.dataset.jvAtcLabel;
      delete button.dataset.jvAtcLabel;
    }
    button.removeAttribute('aria-busy');
  }

  #setBusyState(disabled) {
    const form = document.getElementById(this.#formId);
    const button = form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (disabled) {
      button.disabled = true;
      button.dataset.configuratorBlocked = 'true';
    } else if (button.dataset.configuratorBlocked === 'true') {
      button.disabled = false;
      delete button.dataset.configuratorBlocked;
    }
  }

  /** Returns an ordered list of missing/invalid fields with human-readable labels. */
  #getMissingFields(state) {
    if (!state) state = this.#getState();
    const missing = [];
    // Check fields in DOM order (top → bottom).
    const checks = [
      { field: 'lug_width_mm', value: state.lug_width_mm },
      { field: 'buckle_width_mm', value: state.buckle_width_mm },
      { field: 'length_code', value: state.length_code },
      { field: 'custom_long_side_mm', value: state.custom_long_side_mm, condition: state.length_code === 'CUSTOM' },
      { field: 'custom_short_side_mm', value: state.custom_short_side_mm, condition: state.length_code === 'CUSTOM' },
      { field: 'stitch_color', value: state.stitch_color, selector: 'input[data-cfg-field="stitch_color_mode"], input[data-cfg-field="stitch_color_custom"]' },
      { field: 'engraving_text', value: state.engraving_text, condition: state.engraving === 'true' },
      { field: 'watch_brand', value: state.watch_brand, condition: state.watch_help === 'true' },
      { field: 'watch_model', value: state.watch_model, condition: state.watch_help === 'true' },
    ];
    for (const check of checks) {
      if (check.condition === false) continue;
      if (check.value) continue;
      const label = this.#getFieldLabel(check.field, check.selector);
      // The selector travels with the entry: stitch_color has no control of its
      // own, so whoever marks or clears it needs to know what to look for.
      if (label) missing.push({ field: check.field, label, selector: check.selector });
    }
    return missing;
  }

  /** Derives a human-readable label from the DOM <label> associated with a field. */
  #getFieldLabel(fieldName, selector) {
    const sel = selector || `[data-cfg-field="${fieldName}"]`;
    const el = this.querySelector(sel);
    if (!el) return fieldName;
    // Find the associated <label> via for attribute or ancestor.
    const id = el.id;
    let labelEl = id ? this.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
    if (!labelEl) labelEl = el.closest('.jv-cfg__field')?.querySelector('.jv-cfg__label');
    if (!labelEl) {
      // Radio groups: look for parent fieldset's legend.
      const legend = el.closest('fieldset')?.querySelector('legend');
      if (legend) return legend.textContent.replace(/\*/g, '').trim();
      return fieldName;
    }
    // Strip the * abbr from label text.
    return labelEl.textContent.replace(/\*/g, '').trim();
  }

  /** Show field-level validation errors + scroll to first missing field. */
  #showValidation() {
    const state = this.#getState();
    const missing = this.#getMissingFields(state);
    if (missing.length === 0) return;

    for (const { field, selector } of missing) {
      this.#setFieldError(field, selector);
    }

    const first = missing[0];
    const el = this.querySelector(first.selector || `[data-cfg-field="${first.field}"]`);
    if (!el) return;

    // Engraving text and the stitch colour live inside the customizer dialog.
    // Walking offsetParent through a closed <dialog> gives zero, so the page
    // scrolled to the top and focus went nowhere — worse than the disabled
    // button this replaces. Open it first and scroll within it.
    const dialog = el.closest('dialog[data-cfg-dialog]');
    if (dialog && !dialog.open) {
      dialog.showModal();
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      });
      return;
    }

    this.#scrollToElement(el);
  }

  /** Scroll an element into view with sticky header offset. */
  #scrollToElement(el) {
    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height') || '60', 10);
    // Use absolute position: walk offsetParent chain for reliable Y.
    let top = 0;
    let node = el;
    while (node) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    window.scrollTo({ top: Math.max(0, top - headerHeight - 20), behavior: 'smooth' });
    setTimeout(() => {
      const focusTarget = el instanceof HTMLSelectElement || el instanceof HTMLInputElement ? el : el.querySelector('input, select');
      focusTarget?.focus({ preventScroll: true });
    }, 400);
  }

  /** Mark a single field as having an error. */
  #setFieldError(fieldName, selector) {
    // stitch_color is not a control — it is whichever of stitch_color_mode or
    // stitch_color_custom is in play, which is why #getMissingFields carries a
    // selector for it. Without honouring that here the lookup found nothing and
    // returned, so the one field that could be missing inside the modal was
    // never marked at all.
    const el = this.querySelector(selector || `[data-cfg-field="${fieldName}"]`);
    if (!el) return;
    const wrapper = el.closest('.jv-cfg__field') || el.closest('fieldset');
    if (!wrapper) return;

    // Set aria-invalid.
    el.setAttribute('aria-invalid', 'true');

    // Add error class to wrapper.
    wrapper.classList.add('jv-cfg__field--error');

    // Insert error message if not already present.
    const errId = `jv-err-${fieldName}`;
    if (!wrapper.querySelector(`#${CSS.escape(errId)}`)) {
      const msg = document.createElement('p');
      msg.id = errId;
      msg.className = 'jv-cfg__field-error';
      msg.setAttribute('role', 'alert');
      msg.textContent = FIELD_ERRORS[fieldName] || `Please select your ${this.#getFieldLabel(fieldName).toLowerCase()}`;
      el.setAttribute('aria-describedby', errId);
      // Insert after the control or after its parent label-row.
      const insertAfter = el.closest('.jv-cfg__label-row')?.nextElementSibling || el;
      insertAfter.after(msg);
    }
  }

  /** Clear error state from a single field. */
  #clearFieldError(fieldName, selector) {
    const el = this.querySelector(selector || `[data-cfg-field="${fieldName}"]`);
    if (!el) return;
    el.removeAttribute('aria-invalid');
    el.removeAttribute('aria-describedby');
    const wrapper = el.closest('.jv-cfg__field') || el.closest('fieldset');
    if (!wrapper) return;
    wrapper.classList.remove('jv-cfg__field--error');
    // Clear by wrapper rather than by id. The error under the stitch swatches
    // is keyed stitch_color while the control that changes is
    // stitch_color_mode, so matching on the name left it on screen after the
    // customer had answered it.
    for (const msg of wrapper.querySelectorAll('.jv-cfg__field-error')) msg.remove();
  }

  /** Clear all field errors. */
  #clearAllFieldErrors() {
    for (const errEl of this.querySelectorAll('.jv-cfg__field-error')) errEl.remove();
    for (const el of this.querySelectorAll('[aria-invalid]')) {
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-describedby');
    }
    for (const el of this.querySelectorAll('.jv-cfg__field--error')) {
      el.classList.remove('jv-cfg__field--error');
    }
  }

  #parseDefaults() {
    const ds = this.dataset;
    this.#defaults.style = DEFAULT_META_MAP.style[ds.defaultStyle?.toLowerCase()] || null;
    this.#defaults.buckle = DEFAULT_META_MAP.buckle[ds.defaultBuckle?.toLowerCase()] || null;
    this.#defaults.watch_end = DEFAULT_META_MAP.watch_end[ds.defaultWatchEnd?.toLowerCase()] || null;
    if (!this.#defaults.style && !this.#defaults.buckle && !this.#defaults.watch_end) return;

    if (this.#defaults.watch_end) {
      const r = this.querySelector(`input[data-cfg-field="watch_end_shape"][value="${this.#defaults.watch_end}"]`);
      if (r) r.checked = true;
    }
    if (this.#defaults.style) {
      const r = this.querySelector(`input[data-cfg-field="style"][value="${this.#defaults.style}"]`);
      if (r) r.checked = true;
    }
    if (this.#defaults.buckle) {
      const r = this.querySelector(`input[data-cfg-field="buckle_family"][value="${this.#defaults.buckle}"]`);
      if (r) r.checked = true;
    }
    this.#syncDefaultBuckleFinish();
    this.#updateOptionLabels();
  }

  #syncDefaultBuckleFinish() {
    const family = this.#radioVal('buckle_family') || 'CLASSIC';
    for (const panel of this.querySelectorAll('[data-buckle-panel]')) {
      const isDefaultPanel = this.#defaults.buckle && panel.dataset.bucklePanel === this.#defaults.buckle;
      const isActive = panel.dataset.bucklePanel === family;
      const legend = panel.querySelector('.jv-cfg__microcopy');
      const finishes = panel.querySelectorAll('input[data-cfg-field="buckle_finish"]');
      if (isDefaultPanel && isActive) {
        finishes.forEach(r => { r.checked = false; r.removeAttribute('required'); });
        if (legend) {
          if (!legend.dataset.originalText) legend.dataset.originalText = legend.textContent;
          legend.textContent = 'Optional — leave unselected to match the photo.';
        }
      } else if (isActive) {
        finishes.forEach(r => r.setAttribute('required', ''));
        if (legend?.dataset.originalText) legend.textContent = legend.dataset.originalText;
      }
    }
  }

  #updateOptionLabels() {
    const axes = [
      { axis: 'watch_end', field: 'watch_end_shape', prefix: '· ' },
      { axis: 'style', field: 'style', prefix: '' },
      { axis: 'buckle', field: 'buckle_family', prefix: '' },
    ];
    for (const { axis, field, prefix } of axes) {
      if (!this.#defaults[axis]) continue;
      for (const radio of this.querySelectorAll(`input[data-cfg-field="${field}"]`)) {
        const small = radio.closest('label')?.querySelector('small');
        if (!small) continue;
        // The card's own description — "Padded", "Unpadded" — comes from the
        // block, so there is one copy of it. Rewriting the label used to drop
        // it: Signature went from "Padded · +$3" to a bare "+$3" the moment the
        // script ran on a product that declares a default style.
        const parts = this.#getOptionLabel(axis, radio.value, small.dataset.desc || '');
        if (!parts) continue;

        // Rebuilt as nodes, not as a string. Writing textContent here wiped the
        // span the block renders around the price, so any card this touched —
        // every buckle on a product that declares a default — lost the emphasis
        // and the price went back to grey small print.
        small.replaceChildren();
        const muted = prefix + parts.muted;
        if (muted) small.append(muted);
        if (parts.price) {
          const priceEl = document.createElement('span');
          priceEl.className = 'jv-cfg__price';
          priceEl.textContent = parts.price;
          small.append(priceEl);
        }
      }
    }
  }

  /**
   * Splits an option's label into the part that stays muted and the price.
   *
   * Two pieces rather than one string so the caller can give the price its own
   * element: "As pictured · Included" and "No extra cost" are descriptions and
   * read quietly, a surcharge is a figure the customer is agreeing to pay.
   *
   * @returns {{muted: string, price: string | null} | null}
   */
  #getOptionLabel(axis, key, desc = '') {
    const defaultKey = this.#defaults[axis];
    if (!defaultKey) return null;

    // "Unpadded · As pictured · Included" says the same thing twice, so a card
    // that carries its own description drops the "As pictured" half.
    const lead = desc ? `${desc} · ` : '';

    if (key === defaultKey) return { muted: desc ? `${desc} · Included` : 'As pictured · Included', price: null };

    const selectedPrice = this.#getAxisOptionPrice(axis, key);
    const defaultPrice = this.#getAxisOptionPrice(axis, defaultKey);
    if (selectedPrice <= defaultPrice) return { muted: `${lead}No extra cost`, price: null };
    return { muted: lead, price: this.#formatSurcharge(selectedPrice) };
  }

  /** The add-on variant's price, in dollars. Zero when the variant is missing. */
  #surchargePrice(surchargeKey) {
    const sku = SURCHARGES[surchargeKey]?.sku;
    const addon = sku ? this.#addonMap[sku] : null;
    return addon?.price_cents != null ? addon.price_cents / 100 : 0;
  }

  /** "+$12" for a round figure, "+$4.95" otherwise — matching the Liquid labels. */
  #formatSurcharge(amount) {
    return `+$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
  }

  #getAxisOptionPrice(axis, key) {
    const surchargeKey = AXIS_SURCHARGE_KEYS[axis]?.[key];
    return surchargeKey ? this.#surchargePrice(surchargeKey) : 0;
  }

  #isIncluded(axis, key) {
    const defaultKey = this.#defaults[axis];
    if (!defaultKey) return false;
    return this.#getAxisOptionPrice(axis, key) <= this.#getAxisOptionPrice(axis, defaultKey);
  }

  #isDefault(axis, key) { return this.#defaults[axis] === key; }

  #summaryOptionValue(axis, key, label) {
    if (this.#isDefault(axis, key)) return `${label} (as pictured)`;
    if (!this.#isIncluded(axis, key)) {
      const price = this.#getAxisOptionPrice(axis, key);
      if (price > 0) return `${label} <span class="jv-cfg__surcharge">${this.#formatSurcharge(price)}</span>`;
    }
    return label;
  }

  #selectVal(name) {
    const element = this.querySelector(`select[data-cfg-field="${name}"]`);
    return element instanceof HTMLSelectElement ? element.value : '';
  }

  #inputVal(name) {
    const element = this.querySelector(`[data-cfg-field="${name}"]`);
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement ? element.value.trim() : '';
  }

  #radioVal(name) {
    const element = this.querySelector(`input[type="radio"][data-cfg-field="${name}"]:checked:not(:disabled)`);
    return element instanceof HTMLInputElement ? element.value : '';
  }

  #checked(name) {
    const element = this.querySelector(`input[type="checkbox"][data-cfg-field="${name}"]`);
    return element instanceof HTMLInputElement && element.checked;
  }

  #summaryRow(label, value) { return `<dt>${this.#escapeHtml(label)}</dt><dd>${value}</dd>`; }
  #formatMoney(amount) { return `$${amount.toFixed(2)}`; }
  #escapeHtml(value) { const div = document.createElement('div'); div.textContent = String(value); return div.innerHTML; }
}

if (!customElements.get('leather-strap-configurator')) customElements.define('leather-strap-configurator', LeatherStrapConfigurator);
